import {Link} from '@tanstack/react-router';
import {ProfileAvatar} from '@/components/profileAvatar/profileAvatar';
import {Typography} from '@/components/ui/typography';
import {cn} from '@/lib/utils';
import type {OrganizerMemberSummary} from '@/apiTypes/organizerMember';

type Props = {
  organizerId: string;
  summary: OrganizerMemberSummary;
  className?: string;
};

/**
 * Summary card for the org-page Events tab. Shows the combined member
 * count and a stack of up to 5 avatars; clicking anywhere navigates to
 * the Members tab. Returns null when the org has no admins or members.
 */
export function MembersTeaser({organizerId, summary, className}: Props) {
  if (summary.totalCount === 0) return null;

  const visibleAvatars = summary.avatars.slice(0, 5);
  const overflow = summary.totalCount - visibleAvatars.length;

  return (
    <Link
      to="/organizer/$organizerId/members"
      params={{organizerId}}
      className={cn('block hover:bg-neutral-50', className)}
    >
      <Typography level="C4" className="font-semibold uppercase tracking-wide">
        Members
      </Typography>
      <Typography level="B2" className="mt-1 text-neutral-700">
        {summary.totalCount} {summary.totalCount === 1 ? 'member' : 'members'}
      </Typography>
      <div className="mt-3 flex items-center">
        <div className="flex -space-x-2">
          {visibleAvatars.map((profile) => (
            <div key={profile.id} className="ring-2 ring-white rounded-full">
              <ProfileAvatar profile={profile} size="sm" />
            </div>
          ))}
        </div>
        {overflow > 0 && (
          <Typography level="C1" className="ml-2 text-neutral-500">
            +{overflow}
          </Typography>
        )}
      </div>
    </Link>
  );
}
